import React, { useState, useRef, useEffect, Fragment } from 'react';
import { Link } from "react-router-dom";
import { Intent } from '@blueprintjs/core';

import { useDebounce } from 'hooks/useDebounce';

import { Tooltip } from 'components/tooltip';
import { FormTextInput } from "components/form-fields";
import { LinkButton, Button, ButtonSave } from 'components/buttons'
import { ListingTable } from "components/listing";
import { ShowHide } from "components/layout";
import { AlertConfirm, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { ManualEntryService } from 'services';
import { HelperFunctions } from 'helpers';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { Filter, FilterButton } from 'components/filtering';


export function ManualEntryTemplateListing() {

    const requiredAction = "Employees:AddManualEntry";
    const manageAction = "Employees:ManualEntryManage";
    const defaultRecordSize = 50;
    const debounceTimeout = 750;

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const tableHeaders = ["Name", "Type", "Rules", "Permissions", "Created By", "Actions"];
    const sortableHeaders = [{ headerName: "Name", sortName: "TEMPLATENAME" }, { headerName: "Type", sortName: "TEMPLATETYPE" }, { headerName: "Rules", sortName: "RULETYPE" }];
    const [selectedTemplateId, setSelectedTemplateId] = useState(0);
    const [selectedTemplateIsOwnedByUser, setSelectedTemplateIsOwnedByUser] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [rawTableData, setRawTableData] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageFilters, setPageFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [loadingData, setLoadingData] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState("You have no templates currently set up.");
    const [tableSortBy, setTableSortBy] = useState(sortableHeaders[0]);
    const [tableSortDir, setTableSortDir] = useState("A");
    const [appliedFilters, setAppliedFilters] = useState([]);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const inputSearchRef = useRef(null);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCloneConfirm, setShowCloneConfirm] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState("");
    const [saving, setSaving] = useState(false);

    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();

    const canAccessPage = UserHasAccess(requiredAction);


    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function deleteTemplate(templateId, ownedByUser) {
        setSelectedTemplateId(templateId);
        setSelectedTemplateIsOwnedByUser(ownedByUser);
        setShowDeleteConfirm(true);
    }

    function cloneTemplate(templateId) {
        setSelectedTemplateId(templateId);
        setShowCloneConfirm(true);
    }

    function onDeleteConfirm() {
        setShowDeleteConfirm(false);

        ManualEntryService.deleteTemplate(selectedTemplateId).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Template has been successfully deleted");
            fetchTableData(true);
            setSelectedTemplateId(0);
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Unable to delete the template, please try again.");
            setSelectedTemplateId(0);
        });
    }

    function onCloneConfirm() {
        setSaving(true);

        ManualEntryService.cloneTemplate(selectedTemplateId, { name: newTemplateName }).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Template has been successfully duplicated");
            fetchTableData(true);
            setShowCloneConfirm(false);
            setNewTemplateName("");
        }, function (error) {
            const validationError = 400;
            if (error && error.status === validationError) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "There was an error cloning the template, please try again");
            }

        }).finally(() => {
            setSaving(false);
        });

    }

    function onNewTemplateNameChange(event) {
        setNewTemplateName(event.target.value);
    }

    function onDeleteCancel() {
        setSelectedTemplateId(0);
        setSelectedTemplateIsOwnedByUser(false);
        setShowDeleteConfirm(false);
    }

    function onCloneCancel() {
        setSelectedTemplateId(0);
        setShowCloneConfirm(false);
        setNewTemplateName("");
    }

    function onFilterChange(listingFilters) {
        setInitalSetupComplete(true);
        setPageNumber(1);
        setNoDataMessage("No results found, please try a different filter.");
        setAppliedFilters(listingFilters);

        if (listingFilters.length > appliedFilters.length) {
            setShowFilters(true);
        }
    }

    function toggleVisibility(templateId, visible) {

        function toggleList(newVisibility) {
            setRawTableData((prev) => {
                let clonedItems = [...prev];
                let itemIndex = clonedItems.findIndex(item => item.id === templateId);

                if (itemIndex > -1) {
                    clonedItems[itemIndex].visible = newVisibility;
                }

                return clonedItems;
            });
        }

        ManualEntryService.toggleTemplateVisibility(templateId, { visable: visible }).then(() => {
            NotificationToaster.show("success", `Template will now be ${visible ? "visible" : "hidden from you on Manage My Driver"}`);
        }, () => {
            NotificationToaster.show("danger", "Could not change the template status, please try again");
            toggleList(!visible);
        });

        toggleList(visible);
    }

    function fetchTableData(forceRefresh) {
        if (canAccessPage && initalSetupComplete) {

            if (currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentTableSortBy.current === tableSortBy &&
                currentTableSortDir.current === tableSortDir &&
                currentAppliedFilters.current === appliedFilters &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentSearchTerm.current = debouncedSearchTerm;
            currentTableSortBy.current = tableSortBy;
            currentTableSortDir.current = tableSortDir;
            currentAppliedFilters.current = appliedFilters;

            setLoadingData(true);

            ManualEntryService.getTemplateList(pageSize, pageNumber, debouncedSearchTerm, tableSortBy.sortName, tableSortDir, appliedFilters).then(function (r) {
                setRawTableData(r.data);
                setTotalRecords(r.totalCount);
                setLoadingData(false);
                setTimeout(function () {
                    if (inputSearchRef.current != null) {
                        inputSearchRef.current.focus();
                    }
                });
            });
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [pageSize, pageNumber, tableSortBy, tableSortDir, debouncedSearchTerm, appliedFilters, initalSetupComplete]);

    useEffect(() => {
        const templateTypes = pageFilters.find(filter => filter.name === "Template")?.items || [];

        if (rawTableData != null) {

            setTableRows(rawTableData.map((d) => {
                const templateType = templateTypes.find(type => type.name === d.type)?.id || "";
                return [
                    <Fragment key={`name-${d.id}`}>
                        <Link to={`/employee-settings/manual-entry-templates/${templateType}/${d.id}`}>{d.name}</Link>
                    </Fragment>,
                    d.type,
                    d.ruleSet,
                    d.permissions,
                    d.createdBy,
                    <div className="button-row-small" key={`actions-${d.id}`}>
                        <Tooltip content="Duplicate">
                            <Button onClick={() => cloneTemplate(d.id)} icon="duplicate" iconOnly={true} large={false} minimal={true}></Button>
                        </Tooltip>
                        <Tooltip content={d.visible ? "Hide template for you" : "Show template for you"}>
                            <Button intent={d.visible ? "primary" : ""} onClick={() => toggleVisibility(d.id, !d.visible)} icon={d.visible ? "eye-open" : "eye-off"} iconOnly={true} large={false} minimal={true}></Button>
                        </Tooltip>
                        <Tooltip content={d.deleteable ? "Delete" : "You do not have permission to delete"}>
                            <Button onClick={() => deleteTemplate(d.id, d.owner)} icon="trash" iconOnly large={false} minimal disabled={!d.deleteable}></Button>
                        </Tooltip>
                    </div>
                ];
            }));
        }

    }, [rawTableData, pageFilters]);

    useEffect(() => {

        const mapFilter = function (list) {
            return HelperFunctions.sortAlphabetically(list.map(function (item) {
                return { "id": item.key, "name": item.name }
            }), "name");
        }

        ManualEntryService.getOptions().then(function (options) {

            var filterArray = [
                {
                    "name": "Template", "items": [
                        ...mapFilter(options.templateTypes)
                    ]
                },
                {
                    "name": "Rules", "items": [
                        ...mapFilter(options.rulesets)
                    ]
                },
                {
                    "name": "Permissions", "items": [
                        ...mapFilter(options.accessTypes)
                    ]
                },
                {
                    "name": "Visible", "singleSelect": true, "items": [
                        { "id": "True", "name": "Visible" },
                        { "id": "False", "name": "Hidden" }
                    ]
                }
            ];

            setPageFilters(filterArray);

        });
    }, []);

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">

                    <h2>Manual Entry Templates</h2>

                    <div className="button-row button-row-stacked-mobile spacer-bottom">
                        <div>
                            <UserAccess perform={manageAction}
                                yes={() => (
                                    <LinkButton intent="primary" text="New template" href="/employee-settings/manual-entry-templates/create" id="new-manual-entry-template" />
                                )}
                            />
                        </div>
                    </div>

                    <ShowHide
                        evaluator={tableRows.length === 0 && !loadingData && debouncedSearchTerm === "" && appliedFilters.length === 0 && initalSetupComplete}
                        hide={(
                            <div className="filter-controls">
                                <div className="inline-items spacer-bottom">
                                    <div className="inline-item fill">
                                        <div className="form-field-full">
                                            <FormTextInput inputRef={inputSearchRef} placeholder="Search Templates" onChange={onSearchChange} value={searchTerm} large disabled={loadingData} icon="search" id="user-search-field" />
                                        </div>
                                    </div>
                                    <div className="inline-item">
                                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                    </div>
                                </div>
                                <Filter filterName="manualEntryTemplates" visible={showFilters} filters={pageFilters} onUpdate={onFilterChange} onClickOutside={onToggleFilter} numOfColumns={3} />
                            </div>
                        )}
                    />

                    


                    <ListingTable
                        id="listing-table-templates"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={loadingData}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                        showPerPage={false}
                    />

                    <AlertConfirm
                        title="Please confirm"
                        isOpen={showDeleteConfirm}
                        onConfirm={onDeleteConfirm}
                        onCancel={onDeleteCancel}
                    >
                        <p>Are you sure you want to delete this template?</p>
                        <ShowHide
                            evaluator={selectedTemplateIsOwnedByUser}
                            hide={(
                                <p><strong>This template might be being used by other users.</strong></p>
                            )}
                        />
                    </AlertConfirm>

                    <Modal
                        isOpen={showCloneConfirm}
                        clickOutsideClose={!saving}
                        title={'New Template Name'}
                        onClose={onCloneCancel}
                        size={ModalSize.SMALL}
                    >

                        <div className="bp6-dialog-body">
                            <p>Please enter the name for your new template:</p>
                            <FormTextInput
                                value={newTemplateName}
                                onChange={onNewTemplateNameChange}
                                disabled={saving}
                            />
                        </div>

                        <div className="bp6-dialog-footer">

                            <div className="bp6-dialog-footer-actions">
                                <Button
                                    text="Cancel"
                                    intent="none"
                                    disabled={saving}
                                    onClick={onCloneCancel}
                                ></Button>
                                <ButtonSave
                                    id="manual-entry-template-clone"
                                    onClick={onCloneConfirm}
                                    intent="primary"
                                    icon="duplicate"
                                    simpleDisabled={newTemplateName?.trim().length === 0}
                                    disabled={saving}
                                    text={'Duplicate'}
                                ></ButtonSave>
                            </div>
                        </div>


                    </Modal>

                </div >
            )
            }
            no={() => (
                <PageRestricted />
            )}
        />
    );

}